<template>
  <section class="lighting-advisor-tiles" data-testid="lighting-advisor-tiles">
    <div v-if="store.initialLoadError" class="lighting-advisor-tiles__error">
      {{
        $textDictionary['lightingAdvisor.error.loadQuestions'] ??
        'Could not load lighting advisor.'
      }}
    </div>

    <template v-else>
      <div class="lighting-advisor-tiles__header">
        <Button
          type="button"
          style-type="attraction-secondary"
          icon-type="icon-right"
          :icon-name="isExpanded ? 'chevron-up' : 'chevron-down'"
          :aria-expanded="isExpanded"
          aria-controls="lighting-advisor-panel"
          data-testid="lighting-advisor-toggle"
          @click="isExpanded = !isExpanded"
        >
          {{ headline }}
        </Button>
      </div>

      <Transition
        :css="false"
        @enter="onPanelEnter"
        @after-enter="onPanelAfterEnter"
        @leave="onPanelLeave"
        @after-leave="onPanelAfterLeave"
      >
        <div
          v-if="isExpanded"
          id="lighting-advisor-panel"
          class="lighting-advisor-tiles__panel"
        >
          <div
            v-if="isQuestionsLoading"
            class="row lighting-advisor-tiles__grid lighting-advisor-tiles__loading"
            data-testid="lighting-advisor-loading"
          >
            <div
              v-for="index in 3"
              :key="`lighting-advisor-tile-skeleton-${index}`"
              class="col-12 col-lg-4"
            >
              <LoadingSkeleton
                class="lighting-advisor-tiles__card-skeleton"
                type="block"
              />
            </div>
          </div>
          <div v-else class="row lighting-advisor-tiles__grid">
            <div
              v-for="section in sections"
              :key="section.sectionId"
              class="col-12 col-lg-4"
              :data-testid="`lighting-advisor-tile-${section.number}`"
            >
              <div class="lighting-advisor-tiles__card">
                <h4 class="lighting-advisor-tiles__card-headline">
                  {{ section.headline }}
                </h4>

                <LightingAdvisorQuestionFields
                  layout="plp-list"
                  :questions="section.questions"
                  :answers="store.answers"
                  :is-question-visible="store.isQuestionVisible"
                  :get-tooltip-keys="store.getTooltipKeys"
                  :get-warning-notes="store.getWarningNotes"
                  @update:answer="store.setAnswer"
                  @tooltip-opened="trackQuestionInformation"
                />

                <p
                  v-if="section.sectionId === 'a2s2' && tile2Summary"
                  class="lighting-advisor-tiles__summary"
                  data-testid="lighting-advisor-tile-2-summary"
                >
                  {{ tile2Summary }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </template>
  </section>
</template>

<script setup lang="ts">
import { Button, LoadingSkeleton } from '@hypercodestudio/basler-components';

import { useLightingAdvisorAnalytics } from '~/composables/useLightingAdvisorAnalytics';
import { useLightingAdvisorStore } from '~/stores/lightingAdvisorStore/lightingAdvisorStore';
import type { LightingAdvisorQuestion } from '~/types/lightingAdvisor';
import { dictText } from '~/utils/lightingAdvisor/questionsCatalog';
import LightingAdvisorQuestionFields from '~/components/global/lib/LightingAdvisor/LightingAdvisorQuestionFields.vue';

const { $textDictionary } = useNuxtApp();
const store = useLightingAdvisorStore();
const analytics = useLightingAdvisorAnalytics();

// Section starts collapsed; questions load only on first expand (no idle API call).
const isExpanded = ref(false);
const questionsLoadStarted = ref(false);

const isQuestionsLoading = computed(
  () =>
    questionsLoadStarted.value &&
    store.isApplying &&
    store.questions.length === 0 &&
    !store.initialLoadError
);

const PANEL_TRANSITION_MS = 300;

watch(isExpanded, (expanded) => {
  if (expanded) {
    analytics.trackEvent('lightingAdvisor_open', { entry: 'plp' });
  }
  if (expanded && !questionsLoadStarted.value) {
    questionsLoadStarted.value = true;
    void store.loadQuestions();
  }
});

/**
 * Lightweight collapsible: clip with `overflow: hidden` only while the height
 * transition runs, then switch to `height: auto` + `overflow: visible` so the
 * cards' large drop shadows are not clipped in the resting (open) state.
 */
function onPanelEnter(el: Element, done: () => void) {
  const panel = el as HTMLElement;
  panel.style.overflow = 'hidden';
  panel.style.height = '0px';
  // Force reflow so the browser registers the start height before animating.
  void panel.offsetHeight;
  panel.style.transition = `height ${PANEL_TRANSITION_MS}ms ease`;
  panel.style.height = `${panel.scrollHeight}px`;
  window.setTimeout(done, PANEL_TRANSITION_MS);
}

function onPanelAfterEnter(el: Element) {
  const panel = el as HTMLElement;
  panel.style.transition = '';
  panel.style.height = 'auto';
  panel.style.overflow = 'visible';
}

function onPanelLeave(el: Element, done: () => void) {
  const panel = el as HTMLElement;
  panel.style.overflow = 'hidden';
  panel.style.height = `${panel.scrollHeight}px`;
  void panel.offsetHeight;
  panel.style.transition = `height ${PANEL_TRANSITION_MS}ms ease`;
  panel.style.height = '0px';
  window.setTimeout(done, PANEL_TRANSITION_MS);
}

function onPanelAfterLeave(el: Element) {
  const panel = el as HTMLElement;
  panel.style.transition = '';
  panel.style.height = '';
  panel.style.overflow = '';
}

function trackQuestionInformation(question: LightingAdvisorQuestion): void {
  const stepNumber = Number(question.id.charAt(3));
  analytics.trackQuestionInformation(
    dictText(
      ($textDictionary.value ?? {}) as Record<string, string | undefined>,
      `lightingAdvisor.step.${stepNumber}.title`
    ),
    question.label || question.content
  );
}

const headline = computed(
  () =>
    $textDictionary.value['lightingAdvisor.plp.headline'] ??
    'Guided lighting selection'
);

const sections = computed(() => [
  {
    sectionId: 'a2s1' as const,
    number: 1,
    headline:
      $textDictionary.value['lightingAdvisor.plp.section.application'] ??
      $textDictionary.value['lightingAdvisor.modal.timeline.step1'] ??
      'Application',
    questions: store.questions.filter((question) =>
      question.id.startsWith('a2s1')
    )
  },
  {
    sectionId: 'a2s2' as const,
    number: 2,
    headline:
      $textDictionary.value['lightingAdvisor.plp.section.dimensions'] ??
      $textDictionary.value['lightingAdvisor.modal.timeline.step2'] ??
      'Dimensions',
    questions: store.questions.filter((question) =>
      question.id.startsWith('a2s2')
    )
  },
  {
    sectionId: 'a2s3' as const,
    number: 3,
    headline:
      $textDictionary.value['lightingAdvisor.plp.section.color'] ??
      $textDictionary.value['lightingAdvisor.modal.timeline.step3'] ??
      'Color',
    questions: store.questions.filter((question) =>
      question.id.startsWith('a2s3')
    )
  }
]);

const tile2Summary = computed(() => {
  const results = store.lastResults;
  if (!results) {
    return '';
  }
  const parts: string[] = [];
  if (results.working_distance != null) {
    const label =
      $textDictionary.value['lightingAdvisor.summary.workingDistance'] ??
      'Working distance';
    parts.push(`${label}: ${results.working_distance} mm`);
  }
  const length = results.max_object_size?.length;
  const width = results.max_object_size?.width;
  if (length != null && width != null) {
    const label =
      $textDictionary.value['lightingAdvisor.summary.objectSize'] ??
      'Object size';
    parts.push(`${label}: ${length} × ${width} mm`);
  }
  return parts.join(' · ');
});

onUnmounted(() => {
  void store.resetAll({ reloadProducts: false });
});
</script>

<style scoped>
.lighting-advisor-tiles__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.lighting-advisor-tiles__panel {
  /* Resting overflow is set to visible via JS after the open transition so the
     card shadows can render; the inline overflow only clips during animation. */
  will-change: height;
}

.lighting-advisor-tiles__grid {
  margin-top: 1rem;
}

.lighting-advisor-tiles__card-skeleton {
  display: block;
  width: 100%;
  min-height: 12rem;
  border-radius: 0.75rem;
}

.lighting-advisor-tiles__card {
  height: 100%;
  padding: 1.25rem;
  border-radius: 0.75rem;
  background: var(--color-surface-default, #fff);
  box-shadow:
    0 11px 24px 0 rgba(0, 0, 0, 0.1),
    0 44px 44px 0 rgba(0, 0, 0, 0.09),
    0 99px 59px 0 rgba(0, 0, 0, 0.05),
    0 176px 70px 0 rgba(0, 0, 0, 0.01);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.lighting-advisor-tiles__card-headline {
  margin: 0;
}

.lighting-advisor-tiles__summary {
  font-size: 0.875rem;
  margin: 0;
}

.lighting-advisor-tiles__error {
  color: var(--color-error, #c00);
}
</style>
